import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Loader2, Building2, Users, TrendingDown, ArrowLeft, Briefcase, Sparkles, Package, MapPin, Globe, Linkedin, Mail, Phone, Plus, Edit, Trash2, FolderOpen, Newspaper, DollarSign, BriefcaseBusiness, ExternalLink, Code, History, UserPlus, RefreshCw, Target } from 'lucide-react';
import RecruiterLayout from '@/components/RecruiterLayout';
import CompanyPeopleForm from '@/components/CompanyPeopleForm';
import CompanyForm from '@/components/CompanyForm';
import CompanyProjectsTab from '@/components/company/CompanyProjectsTab';
import { useToast } from '@/hooks/use-toast';
import { LushaDiscoverButton } from '@/components/lusha';
import { AIEnrichButton } from '@/components/company/AIEnrichButton';
import { AIEnrichedBadge } from '@/components/company/AIEnrichedBadge';
import { CompanyNewsTab } from '@/components/company/CompanyNewsTab';
import { CompanyFundingTab } from '@/components/company/CompanyFundingTab';
import { CompanyTechStackTab } from '@/components/company/CompanyTechStackTab';
import { CompanyActivityTab } from '@/components/company/CompanyActivityTab';

const CRM_STATUSES = ['not_prospect', 'lead', 'prospect', 'client', 'on_hold', 'lost'] as const;
const COMPANY_CATEGORIES = ['services', 'staffing', 'end_client', 'unknown', 'recruiter', 'it_company', 'staff_augmentor', 'consulting'] as const;

interface CompanyCandidate {
  id: string;
  first_name: string;
  last_name: string;
  email_primary: string;
  job_title: string;
  is_current: boolean;
  start_date: string;
  end_date: string | null;
}

interface SkillProfile {
  skill_name: string;
  skill_type: string;
  candidate_count: number;
  avg_years: number;
}

interface OutboundCandidate {
  id: string;
  first_name: string;
  last_name: string;
  left_date: string;
  last_role: string;
  current_status: string;
  skills: string[];
}

interface OpenPosition {
  id: string;
  title: string;
  location: string | null;
  job_type: string | null;
  department: string | null;
  experience_level: string | null;
  is_remote: boolean;
  source_url: string | null;
  discovered_at: string;
}

const CRM_STATUS_COLORS: Record<string, string> = {
  not_prospect: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
  lead: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  prospect: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  client: 'bg-green-500/10 text-green-600 border-green-500/20',
  on_hold: 'bg-gray-500/10 text-gray-600 border-gray-500/20',
  lost: 'bg-red-500/10 text-red-600 border-red-500/20',
};

const CompanyIntelligence = () => {
  const { companyId } = useParams();
  const { toast } = useToast();
  const [company, setCompany] = useState<any>(null);
  const [candidates, setCandidates] = useState<CompanyCandidate[]>([]);
  const [skillProfile, setSkillProfile] = useState<SkillProfile[]>([]);
  const [outboundCandidates, setOutboundCandidates] = useState<OutboundCandidate[]>([]);
  const [products, setProducts] = useState<{ product: string; count: number }[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [openPositions, setOpenPositions] = useState<OpenPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [companyFormOpen, setCompanyFormOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<any>(null);
  const [activeTab, setActiveTab] = useState('contacts');

  useEffect(() => {
    if (companyId) {
      fetchCompanyIntelligence();
    }
  }, [companyId]);

  const fetchCompanyIntelligence = async () => {
    try {
      // Fetch company details
      const { data: companyData } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();
      
      setCompany(companyData);

      // Fetch contacts (AIR-170, AIR-177)
      const { data: contactsData } = await supabase
        .from('company_people')
        .select('*')
        .eq('company_id', companyId)
        .order('is_primary', { ascending: false });
      
      setContacts(contactsData || []);

      // Fetch open positions
      const { data: positionsData } = await supabase
        .from('company_open_positions')
        .select('*')
        .eq('company_id', companyId)
        .order('discovered_at', { ascending: false });
      
      setOpenPositions(positionsData || []);

      // Fetch all candidates who worked at this company (AIR-84)
      const { data: experienceData } = await supabase
        .from('candidate_experience')
        .select(`
          id,
          job_title,
          start_date,
          end_date,
          is_current,
          products_worked,
          candidates (
            id,
            first_name,
            last_name,
            email_primary,
            availability_status
          )
        `)
        .eq('company_id', companyId);

      if (experienceData) {
        // Deduplicate candidates - if same person has multiple experiences, 
        // keep the most recent or current one
        const candidateMap = new Map<string, CompanyCandidate>();
        experienceData
          .filter((exp: any) => exp.candidates)
          .forEach((exp: any) => {
            const candidateId = exp.candidates.id;
            const existingEntry = candidateMap.get(candidateId);
            
            // Prioritize current roles, then most recent start date
            const shouldReplace = !existingEntry || 
              (exp.is_current && !existingEntry.is_current) ||
              (!existingEntry.is_current && !exp.is_current && 
                new Date(exp.start_date || 0) > new Date(existingEntry.start_date || 0));
            
            if (shouldReplace) {
              candidateMap.set(candidateId, {
                id: exp.candidates.id,
                first_name: exp.candidates.first_name,
                last_name: exp.candidates.last_name,
                email_primary: exp.candidates.email_primary,
                job_title: exp.job_title,
                is_current: exp.is_current,
                start_date: exp.start_date,
                end_date: exp.end_date,
              });
            }
          });
        setCandidates(Array.from(candidateMap.values()));

        // Extract products (AIR-88)
        const productMap = new Map<string, number>();
        experienceData.forEach((exp: any) => {
          if (exp.products_worked) {
            exp.products_worked.forEach((product: string) => {
              productMap.set(product, (productMap.get(product) || 0) + 1);
            });
          }
        });
        setProducts(
          Array.from(productMap.entries())
            .map(([product, count]) => ({ product, count }))
            .sort((a, b) => b.count - a.count)
        );

        // Identify outbound candidates (AIR-86)
        const outbound: OutboundCandidate[] = [];
        for (const exp of experienceData) {
          const candidateData = exp.candidates as any;
          if (!exp.is_current && exp.end_date && candidateData) {
            // Get candidate skills
            const { data: skillsData } = await supabase
              .from('candidate_skills')
              .select('skills(name)')
              .eq('candidate_id', candidateData.id);
            
            const skills = skillsData?.map((s: any) => s.skills?.name).filter(Boolean) || [];
            
            outbound.push({
              id: candidateData.id,
              first_name: candidateData.first_name,
              last_name: candidateData.last_name,
              left_date: exp.end_date,
              last_role: exp.job_title,
              current_status: candidateData.availability_status || 'unknown',
              skills,
            });
          }
        }
        setOutboundCandidates(outbound.sort((a, b) => 
          new Date(b.left_date).getTime() - new Date(a.left_date).getTime()
        ));
      }

      // Fetch aggregated skill profile - ONLY skills linked to experiences at this company (AIR-85)
      // Get experience IDs for this company
      const experienceIds = experienceData?.map((e: any) => e.id) || [];
      
      if (experienceIds.length > 0) {
        // Only fetch skills that are explicitly linked to experiences at this company
        const { data: skillsData } = await supabase
          .from('candidate_skills')
          .select(`
            years_experience,
            experience_id,
            skills (name, type)
          `)
          .in('experience_id', experienceIds);

        if (skillsData) {
          const skillMap = new Map<string, { type: string; count: number; totalYears: number }>();
          skillsData.forEach((cs: any) => {
            const name = cs.skills?.name;
            const type = cs.skills?.type || 'unknown';
            if (name) {
              const existing = skillMap.get(name) || { type, count: 0, totalYears: 0 };
              skillMap.set(name, {
                type,
                count: existing.count + 1,
                totalYears: existing.totalYears + (cs.years_experience || 0),
              });
            }
          });

          setSkillProfile(
            Array.from(skillMap.entries())
              .map(([name, data]) => ({
                skill_name: name,
                skill_type: data.type,
                candidate_count: data.count,
                avg_years: data.count > 0 ? data.totalYears / data.count : 0,
              }))
              .sort((a, b) => b.candidate_count - a.candidate_count)
          );
        }
      }
    } catch (error) {
      console.error('Error fetching company intelligence:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async () => {
    const { data } = await supabase
      .from('company_people')
      .select('*')
      .eq('company_id', companyId)
      .order('is_primary', { ascending: false });
    setContacts(data || []);
  };

  const handleDeleteContact = async (id: string) => {
    if (!confirm('Delete this contact?')) return;
    try {
      const { error } = await supabase.from('company_people').delete().eq('id', id);
      if (error) throw error;
      toast({ title: 'Contact deleted' });
      fetchContacts();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const updateCompanyField = async (field: string, value: string) => {
    try {
      const { error } = await supabase
        .from('companies')
        .update({ [field]: value })
        .eq('id', companyId);
      if (error) throw error;
      setCompany({ ...company, [field]: value });
      toast({ title: `${field === 'crm_status' ? 'Status' : 'Category'} updated` });
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const getSkillTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      technical: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
      soft: 'bg-green-500/10 text-green-700 dark:text-green-400',
      language: 'bg-purple-500/10 text-purple-700 dark:text-purple-400',
      tool: 'bg-orange-500/10 text-orange-700 dark:text-orange-400',
      framework: 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400',
      certification: 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
      product: 'bg-pink-500/10 text-pink-700 dark:text-pink-400',
    };
    return colors[type] || 'bg-gray-500/10 text-gray-700 dark:text-gray-400';
  };

  if (loading) {
    return (
      <RecruiterLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </RecruiterLayout>
    );
  }

  if (!company) {
    return (
      <RecruiterLayout>
        <div className="text-center py-20">
          <p className="text-muted-foreground">Company not found</p>
        </div>
      </RecruiterLayout>
    );
  }

  return (
    <RecruiterLayout>
      <div>
        <div className="mb-6">
          <Link to="/recruiter/companies">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Companies
            </Button>
          </Link>
          
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Building2 className="h-8 w-8 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold">{company.name}</h1>
                <Select
                  value={company.crm_status || 'lead'}
                  onValueChange={(value) => updateCompanyField('crm_status', value)}
                >
                  <SelectTrigger className={`w-auto h-6 text-xs border ${CRM_STATUS_COLORS[company.crm_status || 'lead']}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CRM_STATUSES.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={company.category || 'unknown'}
                  onValueChange={(value) => updateCompanyField('category', value)}
                >
                  <SelectTrigger className="w-auto h-6 text-xs border">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPANY_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat.replace('_', ' ')}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {company.industry && <Badge variant="secondary">{company.industry}</Badge>}
                {company.stock_ticker && (
                  <Badge variant="outline" className="font-mono">
                    <DollarSign className="h-3 w-3 mr-1" />
                    {company.stock_ticker}
                  </Badge>
                )}
                {company.ai_enriched_at && <AIEnrichedBadge enrichedAt={company.ai_enriched_at} />}
              </div>
              
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                {(company.headquarters_city || company.primary_location || company.location) && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {company.headquarters_city && company.headquarters_country 
                      ? `${company.headquarters_city}, ${company.headquarters_country}`
                      : company.primary_location || company.location}
                    {company.secondary_locations?.length > 0 && ` (+${company.secondary_locations.length} more)`}
                  </span>
                )}
                {(company.employee_count || company.employee_count_estimate) && (
                  <span>~{company.employee_count || company.employee_count_estimate} employees</span>
                )}
                {company.website_url && (
                  <a href={company.website_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                    <Globe className="h-3 w-3" /> Website
                  </a>
                )}
                {company.linkedin_url && (
                  <a href={company.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                    <Linkedin className="h-3 w-3" /> LinkedIn
                  </a>
                )}
              </div>
              
              {company.main_business && (
                <p className="mt-2 text-sm text-muted-foreground">{company.main_business}</p>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button variant="outline" onClick={() => setCompanyFormOpen(true)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit Company
              </Button>
              <AIEnrichButton 
                companyId={companyId!}
                companyName={company.name}
                websiteUrl={company.website_url}
                onEnriched={fetchCompanyIntelligence}
              />
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card 
            className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setActiveTab('candidates')}
          >
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-primary" />
              <div>
                <p className="text-2xl font-bold">{candidates.length}</p>
                <p className="text-sm text-muted-foreground">Total Candidates</p>
              </div>
            </div>
          </Card>
          <Card 
            className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setActiveTab('candidates')}
          >
            <div className="flex items-center gap-3">
              <Briefcase className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">
                  {candidates.filter(c => c.is_current).length}
                </p>
                <p className="text-sm text-muted-foreground">Current Employees</p>
              </div>
            </div>
          </Card>
          <Card 
            className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setActiveTab('skills')}
          >
            <div className="flex items-center gap-3">
              <Sparkles className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{skillProfile.length}</p>
                <p className="text-sm text-muted-foreground">Unique Skills</p>
              </div>
            </div>
          </Card>
          <Card 
            className="p-4 cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => setActiveTab('outbound')}
          >
            <div className="flex items-center gap-3">
              <TrendingDown className="h-8 w-8 text-orange-500" />
              <div>
                <p className="text-2xl font-bold">{outboundCandidates.length}</p>
                <p className="text-sm text-muted-foreground">Outbound Talent</p>
              </div>
            </div>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <ScrollArea className="w-full whitespace-nowrap">
            <TabsList className="inline-flex w-max">
              <TabsTrigger value="contacts">Contacts ({contacts.length})</TabsTrigger>
              <TabsTrigger value="news" className="gap-1">
                <Newspaper className="h-4 w-4" />
                News
              </TabsTrigger>
              <TabsTrigger value="openings" className="gap-1">
                <BriefcaseBusiness className="h-4 w-4" />
                Jobs ({openPositions.length})
              </TabsTrigger>
              <TabsTrigger value="funding" className="gap-1">
                <DollarSign className="h-4 w-4" />
                Funding
              </TabsTrigger>
              <TabsTrigger value="techstack" className="gap-1">
                <Code className="h-4 w-4" />
                Tech Stack
              </TabsTrigger>
              <TabsTrigger value="projects">
                <FolderOpen className="h-4 w-4 mr-1" />
                Projects
              </TabsTrigger>
              <TabsTrigger value="candidates">Talent Pool</TabsTrigger>
              <TabsTrigger value="skills">Skills</TabsTrigger>
              <TabsTrigger value="outbound">Outbound</TabsTrigger>
              <TabsTrigger value="products">Products</TabsTrigger>
              <TabsTrigger value="activity" className="gap-1">
                <History className="h-4 w-4" />
                Activity
              </TabsTrigger>
            </TabsList>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          <TabsContent value="contacts" className="space-y-4 mt-4">
            <div className="flex justify-end">
              <Button onClick={() => { setSelectedContact(null); setContactFormOpen(true); }}>
                <Plus className="mr-2 h-4 w-4" />
                Add Contact
              </Button>
            </div>
            {contacts.length > 0 ? (
              <div className="grid gap-3">
                {contacts.map(contact => (
                  <Card key={contact.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{contact.name}</span>
                          {contact.is_primary && <Badge>Primary</Badge>}
                          {contact.is_ai_enriched && <AIEnrichedBadge enrichedAt={contact.ai_enriched_at} />}
                        </div>
                        {(contact.designation || contact.role) && (
                          <p className="text-sm text-muted-foreground">
                            {contact.designation}{contact.designation && contact.role && ' • '}{contact.role}
                          </p>
                        )}
                        <div className="flex flex-wrap gap-4 mt-2 text-sm">
                          {contact.email && (
                            <a href={`mailto:${contact.email}`} className="flex items-center gap-1 text-primary hover:underline">
                              <Mail className="h-3 w-3" /> {contact.email}
                            </a>
                          )}
                          {contact.phone && (
                            <a href={`tel:${contact.phone}`} className="flex items-center gap-1 text-muted-foreground hover:underline">
                              <Phone className="h-3 w-3" /> {contact.phone}
                            </a>
                          )}
                          {contact.linkedin_url && (
                            <a href={contact.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-primary hover:underline">
                              <Linkedin className="h-3 w-3" /> LinkedIn
                            </a>
                          )}
                        </div>
                        {contact.notes && (
                          <p className="text-sm text-muted-foreground mt-2">{contact.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {(!contact.email || !contact.phone) && (
                          <LushaDiscoverButton
                            entityType="company_people"
                            entityId={contact.id}
                            firstName={contact.name.split(' ')[0] || ''}
                            lastName={contact.name.split(' ').slice(1).join(' ') || ''}
                            companyName={company?.name || ''}
                            onSuccess={() => fetchCompanyIntelligence()}
                            size="sm"
                            variant="ghost"
                          />
                        )}
                        <Button variant="ghost" size="sm" onClick={() => { setSelectedContact(contact); setContactFormOpen(true); }}>
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(contact.id)}>
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No contacts added yet</p>
                <p className="text-sm text-muted-foreground mt-1">Add key contacts like hiring managers, executives</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="news" className="mt-4">
            <CompanyNewsTab companyId={companyId!} companyName={company.name} />
          </TabsContent>

          <TabsContent value="openings" className="space-y-4 mt-4">
            {openPositions.length > 0 ? (
              <>
                <Card className="p-4 bg-blue-500/10 border-blue-500/20">
                  <p className="text-sm">
                    <strong>{openPositions.length}</strong> open position(s) discovered for {company.name}. 
                    <span className="text-muted-foreground ml-1">Use AI Enrich to refresh the list.</span>
                  </p>
                </Card>
                <div className="grid gap-3">
                  {openPositions.map(position => (
                    <Card key={position.id} className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            {position.source_url ? (
                              <a 
                                href={position.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="font-semibold text-primary hover:underline"
                              >
                                {position.title}
                              </a>
                            ) : (
                              <span className="font-semibold">{position.title}</span>
                            )}
                            {position.is_remote && (
                              <Badge variant="outline" className="text-xs">Remote</Badge>
                            )}
                            {position.job_type && (
                              <Badge variant="secondary" className="text-xs">{position.job_type}</Badge>
                            )}
                            {position.experience_level && (
                              <Badge variant="outline" className="text-xs">{position.experience_level}</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                            {position.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" /> {position.location}
                              </span>
                            )}
                            {position.department && (
                              <span className="flex items-center gap-1">
                                <Briefcase className="h-3 w-3" /> {position.department}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>Discovered: {new Date(position.discovered_at).toLocaleDateString()}</span>
                            {position.source_url && (
                              <a 
                                href={position.source_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-primary hover:underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                View Job
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            ) : (
              <Card className="p-8 text-center">
                <BriefcaseBusiness className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No open positions discovered yet</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Click "AI Enrich" to search for current job openings at {company.name}
                </p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="projects" className="mt-4">
            <CompanyProjectsTab companyId={companyId!} />
          </TabsContent>

          <TabsContent value="funding" className="mt-4">
            <CompanyFundingTab companyId={companyId!} companyName={company.name} />
          </TabsContent>

          <TabsContent value="techstack" className="mt-4">
            <CompanyTechStackTab companyId={companyId!} companyName={company.name} />
          </TabsContent>

          <TabsContent value="candidates" className="space-y-4 mt-4">
            {candidates.length > 0 ? (
              candidates.map(candidate => (
                <Card key={`${candidate.id}-${candidate.start_date}`} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <Link 
                        to={`/recruiter/candidates?selected=${candidate.id}`}
                        className="font-semibold hover:text-primary hover:underline"
                      >
                        {candidate.first_name} {candidate.last_name}
                      </Link>
                      <p className="text-sm text-primary">{candidate.job_title}</p>
                      <p className="text-xs text-muted-foreground">
                        {candidate.start_date} - {candidate.is_current ? 'Present' : candidate.end_date || 'N/A'}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          toast({ title: 'Add to Pipeline', description: 'Navigate to Jobs to add this candidate to a pipeline' });
                        }}
                      >
                        <UserPlus className="h-4 w-4 mr-1" />
                        Add to Job
                      </Button>
                      <Badge variant={candidate.is_current ? 'default' : 'secondary'}>
                        {candidate.is_current ? 'Current' : 'Former'}
                      </Badge>
                    </div>
                  </div>
                </Card>
              ))
            ) : (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No candidates found for this company</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="skills" className="space-y-4 mt-4">
            {skillProfile.length > 0 ? (
              <div className="grid gap-3">
                {skillProfile.map(skill => (
                  <Card key={skill.skill_name} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className={getSkillTypeColor(skill.skill_type)}>
                          {skill.skill_type}
                        </Badge>
                        <span className="font-medium">{skill.skill_name}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{skill.candidate_count} candidates</span>
                        {skill.avg_years > 0 && (
                          <span>~{skill.avg_years.toFixed(1)} avg years</span>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No skill data available</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="outbound" className="space-y-4 mt-4">
            {outboundCandidates.length > 0 ? (
              <>
                <Card className="p-4 bg-orange-500/10 border-orange-500/20">
                  <p className="text-sm">
                    <strong>{outboundCandidates.length}</strong> candidates have left this company and may be available for recruitment.
                  </p>
                </Card>
                {outboundCandidates.map(candidate => (
                  <Card key={candidate.id} className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className="font-semibold">
                          {candidate.first_name} {candidate.last_name}
                        </span>
                        <p className="text-sm text-muted-foreground">
                          Left as {candidate.last_role} on {candidate.left_date}
                        </p>
                        {candidate.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {candidate.skills.slice(0, 5).map((skill, i) => (
                              <Badge key={i} variant="outline" className="text-xs">
                                {skill}
                              </Badge>
                            ))}
                            {candidate.skills.length > 5 && (
                              <Badge variant="outline" className="text-xs">
                                +{candidate.skills.length - 5} more
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      <Badge 
                        variant={candidate.current_status === 'available' ? 'default' : 'secondary'}
                      >
                        {candidate.current_status}
                      </Badge>
                    </div>
                  </Card>
                ))}
              </>
            ) : (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No outbound talent data available</p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="products" className="space-y-4 mt-4">
            {products.length > 0 ? (
              <div className="grid gap-3">
                {products.map(({ product, count }) => (
                  <Card key={product} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-primary" />
                        <span className="font-medium">{product}</span>
                      </div>
                      <Badge variant="secondary">{count} candidate(s)</Badge>
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">No product experience data available</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Products will appear when candidates add them to their experience
                </p>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="activity" className="mt-4">
            <CompanyActivityTab companyId={companyId!} />
          </TabsContent>
        </Tabs>
      </div>

      <CompanyPeopleForm
        companyId={companyId!}
        person={selectedContact}
        open={contactFormOpen}
        onOpenChange={setContactFormOpen}
        onSuccess={() => {
          fetchContacts();
          setContactFormOpen(false);
        }}
      />

      <CompanyForm
        company={company}
        open={companyFormOpen}
        onOpenChange={setCompanyFormOpen}
        onSuccess={() => {
          fetchCompanyIntelligence();
          setCompanyFormOpen(false);
        }}
      />
    </RecruiterLayout>
  );
};

export default CompanyIntelligence;
